import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { GoogleButton } from "@/components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name || "there"}!`);
      const dest = loc.state?.from || (user.role === "admin" ? "/admin" : "/dashboard");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-110px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-brand-navy grain">
        <img src="https://images.unsplash.com/photo-1528127269322-539801943592?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Vietnam" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <img src="/logo.png" alt="ImmiLaw.VN" className="h-12 w-auto bg-white rounded-lg p-1.5 self-start" />
          <div>
            <h2 className="font-serif text-4xl tracking-tight leading-tight">Welcome back to your immigration dashboard</h2>
            <p className="mt-4 text-slate-300 max-w-md">Track applications, download forms and receipts, and message our consultants securely.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <h1 className="font-serif text-4xl text-brand-navy tracking-tight">Sign in</h1>
          <p className="text-slate-500 mt-2">Access your ImmiLaw.VN account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors" placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Password</label>
              <div className="relative mt-1.5">
                <input required type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 outline-none focus:border-brand-red transition-colors" placeholder="••••••••" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />} Sign in
            </button>
          </form>

          <GoogleButton label="Sign in with Google" />

          <p className="mt-6 text-sm text-slate-500">Don't have an account? <Link to="/register" className="font-semibold text-brand-red hover:underline" data-testid="login-to-register">Create one</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
